import {FC, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {IOfferModel} from '../cors/_models'
import OfferEditForm from './OfferEditForm'
import '../offers-list.css'

/**
 * Right-side drawer wrapper for the edit form. Pure presentation —
 * data fetching is the parent's job (the row in the table already
 * has the offer data when it triggers `onEdit`).
 *
 * Closes on Escape and click-outside (the dim overlay). Body scroll is
 * locked while open to prevent the page jumping when the drawer mounts.
 */

type IProps = {
  offer: IOfferModel | null
  onClose: () => void
  onSaved: (updated: IOfferModel) => void
}

const OfferDrawer: FC<IProps> = ({offer, onClose, onSaved}) => {
  const {formatMessage} = useIntl()
  const open = !!offer

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open || !offer) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ms-drawer-title"
      className="hsb-offers hsb-of-drawer-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="hsb-of-drawer">
        <div className="hsb-of-drawer-head">
          <h4 id="ms-drawer-title" className="hsb-of-drawer-title">
            {formatMessage(
              {id: 'offers.drawer.title'},
              {name: offer.product_name ?? `#${offer.product_supplier_id}`},
            )}
          </h4>
          <button
            type="button"
            className="hsb-of-drawer-close"
            onClick={onClose}
            aria-label={formatMessage({id: 'offers.drawer.close'})}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <OfferEditForm
          // Force a fresh form instance per offer; resets internal state.
          key={offer.product_supplier_id}
          offer={offer}
          onSaved={onSaved}
          onCancel={onClose}
        />
      </div>
    </div>
  )
}

export default OfferDrawer
